import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';

import { 
  UserRequestService, 
  UserRequest, 
  ResearchRequestFilters, 
  RequestStatistics 
} from '../../core/services/user-request.service';
import { PaperRequestService, PaperUploadData } from '../../core/services/paper-request.service';
import { AuthService } from '../../auth/services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../components/common/breadcrumb/breadcrumb.component';
import { ContentLoaderComponent } from '../../components/common/content-loader/content-loader.component';
import { ResearchCardComponent } from '../../components/research/research-card/research-card.component';
import { ModalComponent } from '../../components/common/modal/modal.component';
import { DocumentUploaderComponent } from '../../components/uploaders/document-uploader/document-uploader.component';
import { ButtonComponent } from '../../components/forms/button/button.component';
import { FiltersDropdownComponent } from '../../components/common/filters-dropdown/filters-dropdown.component';
import { FormSelectComponent } from '../../components/forms/select/form-select.component';
import { ContentErrorComponent } from '../../components/common/content-error/content-error.component';
import { PaginationComponent } from '../../components/common/pagination/pagination.component';
import { AuthModalComponent } from '../../auth/components/auth-modal/auth-modal.component';
import { ListingsSearchboxComponent } from '../../components/common/listings-searchbox/listings-searchbox.component';
import { SeoService } from '../../shared/services/seo.service';
import { environment } from '../../../environment/environment';

@Component({
  selector: 'app-research',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumbComponent, ContentLoaderComponent, ResearchCardComponent, AuthModalComponent, ModalComponent, DocumentUploaderComponent, ButtonComponent, FiltersDropdownComponent, FormSelectComponent, FormSelectComponent, ContentErrorComponent, PaginationComponent, ListingsSearchboxComponent],
  templateUrl: './research.component.html',
  styleUrl: './research.component.css'
})
export class ResearchComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [
    { label: '', routerLink: '/home', icon: 'fas fa-home' },
    { label: 'Research Requests', routerLink: '/research', disabled: true }
  ];
  private destroy$ = new Subject<void>();

  // Data properties
  researchRequests: UserRequest[] = [];
  statistics: RequestStatistics | null = null;
  loading = false;
  error: string | null = null;

  // Pagination properties
  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  itemsPerPage = 12;
  hasNextPage = false;
  hasPrevPage = false;

  // Filter properties
  searchQuery = '';
  selectedType: 'Lab' | 'Document' | 'Data' | '' = '';
  sortBy = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';
  filterOptions = {
    typeOptions: [
      {label: 'All', value: ''},
      {label: 'Document', value: 'Document'},
      {label: 'Data', value: 'Data'},
    ]
  }

  // Upload properties
  selectedRequest: UserRequest | null = null;
  uploadLoading = false;
  showUploadModal = false;
  uploadData = {
    file: null as File | null
  };

  // Login modal properties
  showLoginModal = false;
  pendingUploadRequest: UserRequest | null = null;

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  // Filter options
  requestTypes = [
    { value: '', label: 'All Types' },
    { value: 'Document', label: 'Document' },
    { value: 'Lab', label: 'Lab Access' },
    { value: 'Data', label: 'Data' }
  ];

  // Expose Math for template
  Math = Math;

  constructor(
    private userRequestService: UserRequestService,
    private paperRequestService: PaperRequestService,
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private seoService: SeoService
  ) {
    // Setup search debouncing
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.searchQuery = searchTerm;
      this.currentPage = 1;
      this.loadResearchRequests();
    });
  }

  ngOnInit(): void {
    // this.setupTechnicalSEO(
    //   data.title,
    //   data.description,
    //   `${data.createdBy?.firstName} ${data.createdBy?.lastName}` || 'Author',
    //   data.createdBy?.profilePicture
    // );
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchQuery = params['search'];
      }
      if(params['page']){
        this.currentPage = Number(params['page']) || 1
      }
    });
    this.loadResearchRequests();
    this.loadStatistics();
  }
  setupTechnicalSEO(title: string, description: string, authorName: string, image: string) {
    const url = `${environment.siteUrl}${this.router.url}`;

    this.seoService.setMeta({
      title: `${title} | Research Decode`,
      description:description,
      url,
      image
    });

    // this.seoService.setSchema({
    //   "@context": "https://schema.org",
    //   "@type": "Service",
    //   "name": `${title}`,
    //   "provider": {
    //     "@type": "Person",
    //     "name": authorName
    //   }
    // });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeOrder(order: 'asc' | 'desc'){
    this.sortOrder = order;
    this.loadResearchRequests();
  }

  /**
   * Load research requests with current filters
   */
  loadResearchRequests(): void {
    this.loading = true;
    this.error = null;

    const filters: ResearchRequestFilters = {
      page: this.currentPage,
      limit: this.itemsPerPage,
      search: this.searchQuery || undefined,
      type: this.selectedType || undefined,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    };

    this.userRequestService.getResearchRequests(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.researchRequests = response.data;
            this.totalCount = response?.count || 0;
            if (response.meta) {
              this.totalCount = response.meta.totalCount;
              this.currentPage = response.meta.currentPage;
              this.totalPages = response.meta.totalPages;
              this.hasNextPage = response.meta.hasNextPage;
              this.hasPrevPage = response.meta.hasPrevPage;
              this.itemsPerPage = response.meta.itemsPerPage;
            }
          } else {
            this.error = response.message || 'Failed to load research requests';
            this.toastr.error(this.error);
          }
          this.loading = false;
        },
        error: (error) => {
          this.error = 'Failed to load research requests. Please try again.';
          this.toastr.error(this.error);
          this.loading = false;
          console.error('Error loading research requests:', error);
        }
      });
  }

  /**
   * Load request statistics
   */
  loadStatistics(): void {
    this.userRequestService.getRequestStatistics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.statistics = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading statistics:', error);
        }
      });
  }

  /**
   * Handle search input
   */
  onSearchInput(): void {
    const target = this.searchQuery;
    // const target = event.target as HTMLInputElement;
    this.searchSubject.next(target);
  }
  resetSearch(): void {
    this.searchQuery = '';
    // const target = event.target as HTMLInputElement;
    this.searchSubject.next(this.searchQuery);
  }

  /**
   * Handle DOI search
   */
  onDoiSearch(): void {
    if (this.searchQuery.trim()) {
      this.currentPage = 1;
      this.loadResearchRequests();
    }
  }

  /**
   * Handle filter changes
   */
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadResearchRequests();
  }

  /**
   * Handle pagination
   */
  goToPage(page: number): void {
    if (page >= 1 && page !== this.currentPage) {
      this.currentPage = page;
      this.loadResearchRequests();
    }
  }

  /**
   * Go to previous page
   */
  previousPage(): void {
    if (this.hasPrevPage) {
      this.goToPage(this.currentPage - 1);
    }
  }

  /**
   * Go to next page
   */
  nextPage(): void {
    if (this.hasNextPage) {
      this.goToPage(this.currentPage + 1);
    }
  }

  /**
   * Get display text for request type
   */
  getRequestTypeDisplay(type: string): string {
    switch (type) {
      case 'Document': return 'Document Request';
      case 'Lab': return 'Lab Access';
      case 'Data': return 'Data Request';
      default: return type;
    }
  }

  /**
   * Get priority badge class
   */
  getPriorityClass(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'high': return 'badge bg-danger';
      case 'medium': return 'badge bg-warning';
      case 'low': return 'badge bg-success';
      default: return 'badge bg-secondary';
    }
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending': return 'badge bg-warning';
      case 'in progress': return 'badge bg-info';
      case 'approved': return 'badge bg-success';
      case 'rejected': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Get requester full name
   */
  getRequesterName(request: UserRequest): string {
    if (!request.requestBy) {
      return 'Research Community';
    }
    return `${request.requestBy.firstName || ''} ${request.requestBy.lastName || ''}`.trim() || 'Anonymous User';
  }

  /**
   * Get requester profile image with fallback
   */
  getRequesterImage(request: UserRequest): string {
    if (!request.requestBy || !request.requestBy.profilePicture) {
      return 'assets/img/user_icon.png';
    }
    return request.requestBy.profilePicture;
  }

  /**
   * Get request details based on type
   */
  getRequestDetails(request: UserRequest): string {
    switch (request.type) {
      case 'Document':
        if (request.documentDetails) {
          return `${request.documentDetails.title} by ${request.documentDetails.author}`;
        }
        break;
      case 'Lab':
        if (request.labDetails) {
          return `${request.labDetails.nature} - ${request.labDetails.needs}`;
        }
        break;
      case 'Data':
        if (request.dataDetails) {
          return `${request.dataDetails.title} - ${request.dataDetails.description}`;
        }
        break;
    }
    return request.description;
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRequestId(index: number, request: UserRequest): string {
    return request._id;
  }

  /**
   * Get request type icon class
   */
  getRequestTypeIcon(type: string): string {
    switch (type) {
      case 'Document': return 'fas fa-file-alt';
      case 'Lab': return 'fas fa-flask';
      case 'Data': return 'fas fa-database';
      default: return 'fas fa-question-circle';
    }
  }

  /**
   * Handle image loading error
   */
  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/img/user_icon.png';
  }

  /**
   * Get document title from selected request
   */
  getDocumentTitle(): string {
    if (!this.selectedRequest) return '';
    return this.selectedRequest.documentDetails?.title || this.selectedRequest.title || 'Document Request';
  }

  /**
   * Get document authors from selected request
   */
  getDocumentAuthors(): string {
    if (!this.selectedRequest) return '';
    return this.selectedRequest.documentDetails?.author || this.getRequesterName(this.selectedRequest) || 'Unknown Author';
  }

  /**
   * Get document DOI from selected request
   */
  getDocumentDoi(): string {
    if (!this.selectedRequest) return '';
    return this.selectedRequest.documentDetails?.doi || '';
  }

  /**
   * Get pagination array for display
   */
  getPaginationArray(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, this.currentPage - halfRange);
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  /**
   * Check if user is logged in
   */
  isUserLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  /**
   * Handle upload button click
   */
  onUploadClick(request: UserRequest): void {
    if (!this.isUserLoggedIn()) {
      // Store the request to proceed with upload after login
      this.pendingUploadRequest = request;
      this.showLoginModal = true;
      return;
    }

    this.selectedRequest = request;
    this.showUploadModal = true;
    
    // Reset only the file, other values will be auto-populated
    this.uploadData.file = null;
  }

  /**
   * Close upload modal
   */
  closeUploadModal(): void {
    this.showUploadModal = false;
    this.selectedRequest = null;
    this.resetUploadForm();
  }

  /**
   * Handle file selection in modal
   */
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        this.toastr.error('Please select a PDF file only', 'Invalid File Type');
        target.value = '';
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        this.toastr.error('File size must be less than 10MB', 'File Too Large');
        target.value = '';
        return;
      }

      this.uploadData.file = file;
    }
  }

  /**
   * Handle form submission
   */
  onUpload(): void {
    if (!this.selectedRequest || !this.uploadData.file) {
      this.toastr.error('Please select a PDF file to upload', 'Validation Error');
      return;
    }

    this.uploadLoading = true;

    const uploadData: PaperUploadData = {
      userRequestId: this.selectedRequest._id,
      paperDetail: {
        title: this.getDocumentTitle(),
        authors: this.getDocumentAuthors(),
        doi: this.getDocumentDoi() || undefined
      },
      file: this.uploadData.file
    };

    console.log('Upload data:', uploadData);

    this.paperRequestService.fulfillUserRequest(uploadData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.toastr.success('Document uploaded successfully!', 'Upload Success');
            this.closeUploadModal();
            this.loadResearchRequests(); // Refresh the list
          } else {
            this.toastr.error(response.message || 'Upload failed', 'Upload Error');
          }
          this.uploadLoading = false;
        },
        error: (error) => {
          console.error('Upload error:', error);
          this.toastr.error('Failed to upload document. Please try again.', 'Upload Error');
          this.uploadLoading = false;
        }
      });
  }

  /**
   * Reset upload form
   */
  resetUploadForm(): void {
    this.uploadData = {
      file: null
    };
    this.uploadLoading = false;
  }

  /**
   * Upload file directly without additional form fields
   */
  private uploadFileDirectly(file: File): void {
    if (!this.selectedRequest) return;

    this.uploadLoading = true;

    // Use the request details to populate the upload data
    const uploadData: PaperUploadData = {
      userRequestId: this.selectedRequest._id,
      paperDetail: {
        title: this.selectedRequest.documentDetails?.title || this.selectedRequest.description,
        authors: this.getRequesterName(this.selectedRequest),
        doi: this.selectedRequest.documentDetails?.doi || undefined
      },
      file: file
    };

    this.paperRequestService.fulfillUserRequest(uploadData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.toastr.success('Document uploaded successfully!', 'Upload Success');
            this.selectedRequest = null;
            this.loadResearchRequests(); // Refresh the list
          } else {
            this.toastr.error(response.message || 'Upload failed', 'Upload Error');
          }
          this.uploadLoading = false;
        },
        error: (error) => {
          console.error('Upload error:', error);
          this.toastr.error('Failed to upload document. Please try again.', 'Upload Error');
          this.uploadLoading = false;
          this.selectedRequest = null;
        }
      });
  }

  /**
   * Close login modal
   */
  closeLoginModal(): void {
    this.showLoginModal = false;
    this.pendingUploadRequest = null;
  }

  /**
   * Handle successful login from modal
   */
  onLoginSuccess(): void {
    this.toastr.success('Login successful! You can now upload documents.', 'Welcome!');
    this.closeLoginModal();
    
    // If there was a pending upload request, proceed with it
    if (this.pendingUploadRequest) {
      this.selectedRequest = this.pendingUploadRequest;
      this.showUploadModal = true;
      this.uploadData.file = null;
      this.pendingUploadRequest = null;
    }
  }
}
